/**
 *  [2021] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */

/**
 * DynamoDBの1つのテーブルに対応するキャッシュオブジェクト
 *
 * 達成したいこと
 *  - 更新があった情報だけを取得できるようにし、テーブルの全スキャン＆ダウンロードを極力なくす
 *  - テーブル構成に依存しない、汎用的なクラスとして実装する
 *
 * 方針
 *  - IndexedDBはサーバのキャッシュとして使う
 *  - IndexedDBはputに時間がかかる（indexing）ので、メモリ上でも別途データを管理する
 *  - エントリの削除は、deletedフラグを立てるだけ、または完全削除の２種類。基本的には完全削除はサーバ側で判断するものとし、クライアントからは行わない
 *  - 強制的に全スキャン＆ダウンロードする仕組みも用意する
 *
 * 前提条件
 *  - プライマリーキー (string)、セカンダリーキー、およびupdatedAt (number)、がテーブルに設定されていること
 *  - DynamoDBと同じようにハッシュキー（プライマリーキー）とレンジキー（セカンダリーキー）が設定されていて、かつupdatedAtをレンジキーとするLSIが設定されているものとする
 *  - deletedキーが追加できること
 *
 * 使い方
 *  - アプリケーションロジック内で、DynamoDBテーブルに対応するCacheManageオブジェクトをインスタンス化する
 *  - DBにデータを書き込むor更新するときは、DBとCacheManageオブジェクトの両方に書き込む
 *  - DBからデータを削除したいときは、DBおよびCacheManageオブジェクトの該当エントリにdeletedフラグを立てて登録する
 *  - データ１件を取得したい場合は、まずCacheManageオブジェクトに問い合わせる。返答がundefinedだった場合はDBに問い合わせる。返ってきた情報はCacheManageオブジェクトに登録する
 *  - データを一括取得したい場合は、まずCacheManageオブジェクトに最新エントリの時刻を問い合わせ、それをDBに投げてその時刻以降の情報だけを取得する。返ってきた情報はCacheManageオブジェクトに登録する
 */

import {sleep} from '@zettant/commonUtils/utils';
import {CacheManage} from './cacheManageClass';
import {init} from './cacheManageInitializer';

const cacheTable: {[key:string]: CacheManage} = {};


/**
 * テーブル用のキャッシュオブジェクトを新規作成する。
 * オブジェクト群を束にしたcacheTableをこのソースで保持しているので、importすればどこからでも同じデータを参照できる。
 *
 * @param tableName
 * @param primaryKeyName
 * @param secondaryKeyName
 * @param email
 * @param removeLeadTime
 * @param adapters
 * @param localStorageMode {boolean} - trueの場合、IndexedDBではなくlocalStorageにキャッシュする
 */
const prepareTable = async (tableName: string, primaryKeyName: string, secondaryKeyName?: any, email?:string, removeLeadTime?: number, adapters?: any, localStorageMode=false): Promise<CacheManage> => {
  if (!(tableName in cacheTable)) {
    const tbl = await init(tableName, primaryKeyName, secondaryKeyName, email, localStorageMode);
    if (removeLeadTime) tbl.setRemoveLeadTime(removeLeadTime);
    if (adapters) {
      tbl.setup({
        createAdapter: adapters.createAdapter,
        updateAdapter: adapters.updateAdapter,
        bulkCreateAdapter: adapters.bulkCreateAdapter,
        bulkUpdateAdapter: adapters.bulkUpdateAdapter,
        deleteAdapter: adapters.deleteAdapter,
        readAdapter: adapters.readAdapter,
        readAdapter2: adapters.readAdapter2,
        readAllAdapter: adapters.readAllAdapter,
        putMediaAdapter: adapters.putMediaAdapter,
        bulkPutMediaAdapter: adapters.bulkPutMediaAdapter,
        getMediaAdapter: adapters.getMediaAdapter,
        deleteMediaAdapter: adapters.deleteMediaAdapter,
        putPrimaryArchiveAdapter: adapters.putPrimaryArchiveAdapter,
        rawPutAdapter: adapters.rawPutAdapter,
        flushForTestingAdapter: adapters.flushForTestingAdapter,
        archiveForTestingAdapter: adapters.archiveForTestingAdapter,
      });
    }
    cacheTable[tableName] = tbl;
  }
  return cacheTable[tableName];
};


/**
 * テーブル用のキャッシュオブジェクトを取得する。存在しなければ完成するまで待つ。
 *
 * @param tableName
 */
const getTable = async (tableName: string): Promise<CacheManage> => {
  let count = 100;
  do {
    const tbl = cacheTable[tableName];
    if (tbl) return tbl;
    await sleep(50); // 100ms待ってみる
    count -= 1;
  } while (count > 0);
  if (typeof window !== 'undefined') {
    alert('cacheManageの初期化ができませんでした');
  }
  return {} as CacheManage;
};

const getTableSync = (tableName: string): CacheManage => cacheTable[tableName];

/**
 * キャッシュオブジェクト全体を返す
 */
const getAllTables = () => cacheTable;

const removeTable = (tableName: string) => {
  if (tableName in cacheTable) {
    delete cacheTable[tableName];
  }
};

const getKey = (primaryKeyValue: string, secondaryKeyValue?:any): string|string[] => {
  if (secondaryKeyValue) {
    return [primaryKeyValue, secondaryKeyValue];
  }
  return primaryKeyValue;
};


export {getKey, getAllTables, getTableSync, prepareTable, getTable, removeTable};
